import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { AppType } from "client/Api/Apps/common";
import { IFormHandler, useFormHandler } from "client/Forms";
import { EditAppHandle } from "../EditApp";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	EditDocumentsAppViewModel,
	saveDocumentsApp,
	SaveDocumentsAppModel
} from "client/Api/Apps/EditApp/saveApp";

export type EditDocumentsAppProps = {
	initialSettings: EditAppSettings<EditDocumentsAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditDocumentsApp = forwardRef(
	(
		props: EditDocumentsAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveDocumentsAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveDocumentsAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveDocumentsAppModel>(
			formHandler,
			saveDocumentsApp,
			AppType.Documents
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);
EditDocumentsApp.displayName = "EditDocumentsApp";

const validation = createValidationConfiguration<SaveDocumentsAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

function convertToSaveModel(
	initialSettings: EditAppSettings<EditDocumentsAppViewModel>
): SaveDocumentsAppModel {
	const {
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow
	};
}